import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowUpIcon, ArrowDownIcon, Search } from "lucide-react";

interface SchoolClass {
  id: string;
  name: string;
  leads_to_graduation?: boolean;
  next_class?: {
    id: string;
    name: string;
  };
  previous_class?: {
    id: string;
    name: string;
    next_class_id: string;
  } | null;
}

interface Student {
  id: string;
  surname: string;
  other_names?: string;
  class: string;
  school_id: string;
  current_class?: SchoolClass;
  stream?: string;
  last_promoted_at?: string;
  class_streams?: {
    preserve_on_promotion: boolean;
  };
}

interface School {
  id: string;
  name: string;
}

export function ClassPromotionManager() {
  const [isPromoting, setIsPromoting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const { toast } = useToast();

  const { data: schools } = useQuery({
    queryKey: ['schools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data as School[];
    },
  });

  const { data: students, refetch: refetchStudents } = useQuery({
    queryKey: ['students-for-promotion', selectedSchool],
    queryFn: async () => {
      if (!selectedSchool) return [];

      const { data: classesData, error: classesError } = await supabase
        .from('school_classes')
        .select(`
          id,
          name,
          leads_to_graduation,
          next_class:next_class_id(id, name)
        `)
        .eq('school_id', selectedSchool);

      if (classesError) throw classesError;

      // Create a map of previous classes
      const previousClassMap = new Map();
      classesData.forEach(currentClass => {
        if (currentClass.next_class || currentClass.leads_to_graduation) {
          previousClassMap.set(
            currentClass.leads_to_graduation ? 'ARCHIVED' : currentClass.next_class.id,
            {
              id: currentClass.id,
              name: currentClass.name,
              next_class_id: currentClass.leads_to_graduation ? 'ARCHIVED' : currentClass.next_class.id
            }
          );
        }
      });

      // Process classes with previous class info and handle graduating classes
      const classesWithPrevious = classesData.map(cls => ({
        ...cls,
        previous_class: previousClassMap.get(cls.id) || null,
        next_class: cls.leads_to_graduation 
          ? { id: 'ARCHIVED', name: 'ARCHIVED' }
          : cls.next_class
      }));

      const classMap = new Map(classesWithPrevious.map(c => [c.name, c]));

      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select(`
          *,
          class_streams (preserve_on_promotion)
        `)
        .eq('school_id', selectedSchool)
        .order('surname');

      if (studentsError) throw studentsError;

      return studentsData?.map(student => ({
        ...student,
        current_class: classMap.get(student.class)
      })) as Student[];
    },
    enabled: !!selectedSchool,
  });

  const promoteStudents = useMutation({
    mutationFn: async () => {
      setIsPromoting(true);
      try {
        const { data: classes, error: classesError } = await supabase
          .from('school_classes')
          .select('*, next_class:next_class_id(id, name)');

        if (classesError) throw classesError;

        for (const currentClass of classes) {
          const { data: students, error: studentsError } = await supabase
            .from('students')
            .select('*, class_streams(preserve_on_promotion)')
            .eq('class', currentClass.name)
            .is('last_promoted_at', null)
            .is('is_archived', false);

          if (studentsError) throw studentsError;

          if (currentClass.leads_to_graduation) {
            for (const student of students) {
              const { error: archiveError } = await supabase
                .from('students')
                .update({
                  class: 'ARCHIVED',
                  stream: null,
                  is_archived: true,
                  archived_at: new Date().toISOString(),
                  archived_reason: 'GRADUATION',
                  last_promoted_at: new Date().toISOString()
                })
                .eq('id', student.id);

              if (archiveError) throw archiveError;
            }
            continue;
          }

          if (currentClass.next_class) {
            for (const student of students) {
              const updateData: any = {
                class: currentClass.next_class.name,
                last_promoted_at: new Date().toISOString(),
              };

              if (student.stream) {
                const { data: streamExists } = await supabase
                  .from('class_streams')
                  .select('preserve_on_promotion')
                  .eq('name', student.stream)
                  .eq('class_id', currentClass.next_class.id)
                  .single();

                if (!streamExists || !streamExists.preserve_on_promotion) {
                  updateData.stream = null;
                }
              }

              const { error: updateError } = await supabase
                .from('students')
                .update(updateData)
                .eq('id', student.id);

              if (updateError) throw updateError;
            }
          }
        }

        await refetchStudents();
        toast({
          title: "Success",
          description: "Students promoted successfully",
        });
      } catch (error: any) {
        toast({
          title: "Error promoting students",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsPromoting(false);
      }
    },
  });

  const handleIndividualPromotion = async (studentId: string, promote: boolean) => {
    try {
      const student = students?.find(s => s.id === studentId);
      if (!student || !student.current_class) {
        throw new Error("Student or class information not found");
      }

      if (promote && student.current_class.leads_to_graduation) {
        const { error: archiveError } = await supabase
          .from('students')
          .update({
            class: 'ARCHIVED',
            stream: null,
            is_archived: true,
            archived_at: new Date().toISOString(),
            archived_reason: 'GRADUATION',
            last_promoted_at: new Date().toISOString()
          })
          .eq('id', student.id);

        if (archiveError) throw archiveError;
        
        await refetchStudents();
        toast({
          title: "Success",
          description: "Student archived after graduation",
        });
        return;
      }

      const targetClass = promote 
        ? student.current_class.next_class?.name 
        : student.current_class.previous_class?.name;

      if (!targetClass) {
        toast({
          title: "Error",
          description: `No ${promote ? 'next' : 'previous'} class defined for ${student.current_class.name}`,
          variant: "destructive",
        });
        return;
      }

      const updateData: any = {
        class: targetClass,
        last_promoted_at: promote ? new Date().toISOString() : null,
      };

      if (promote && student.stream) {
        const { data: streamExists } = await supabase
          .from('class_streams')
          .select('preserve_on_promotion')
          .eq('name', student.stream)
          .eq('class_id', student.current_class.next_class?.id)
          .single();

        if (!streamExists || !streamExists.preserve_on_promotion) {
          updateData.stream = null;
        }
      }

      const { error: updateError } = await supabase
        .from('students')
        .update(updateData)
        .eq('id', student.id);

      if (updateError) throw updateError;

      await refetchStudents();
      toast({
        title: "Success",
        description: `Student ${promote ? 'promoted' : 'demoted'} successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handlePromoteAll = () => {
    promoteStudents.mutate();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">Manage Student Promotions</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Student Promotion Management</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between sticky top-0 bg-background z-10 py-2">
            <Select
              value={selectedSchool}
              onValueChange={setSelectedSchool}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select a school" />
              </SelectTrigger>
              <SelectContent>
                {schools?.map((school) => (
                  <SelectItem key={school.id} value={school.id}>
                    {school.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedSchool && (
              <>
                <div className="flex-1 max-w-sm relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button
                  onClick={() => setShowPromotionDialog(true)}
                  disabled={isPromoting || !selectedSchool}
                >
                  {isPromoting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isPromoting ? "Promoting..." : "Promote All Eligible Students"}
                </Button>
              </>
            )}
          </div>

          {selectedSchool ? (
            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Current Class</TableHead>
                      <TableHead>Next Class</TableHead>
                      <TableHead>Stream</TableHead>
                      <TableHead>Last Promoted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students?.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>
                          {student.surname}
                          {student.other_names && `, ${student.other_names}`}
                        </TableCell>
                        <TableCell>{student.class}</TableCell>
                        <TableCell>
                          {student.current_class?.leads_to_graduation 
                            ? 'ARCHIVED (Graduation)'
                            : student.current_class?.next_class?.name || 'No next class defined'}
                        </TableCell>
                        <TableCell>{student.stream || '-'}</TableCell>
                        <TableCell>
                          {student.last_promoted_at 
                            ? new Date(student.last_promoted_at).toLocaleDateString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleIndividualPromotion(student.id, false)}
                              disabled={!student.current_class?.previous_class}
                              title={!student.current_class?.previous_class 
                                ? "No previous class defined" 
                                : `Demote to ${student.current_class.previous_class.name}`}
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleIndividualPromotion(student.id, true)}
                              disabled={!student.current_class?.next_class && !student.current_class?.leads_to_graduation}
                              title={student.current_class?.leads_to_graduation 
                                ? "Archive (Graduation)"
                                : !student.current_class?.next_class 
                                  ? "No next class defined" 
                                  : `Promote to ${student.current_class.next_class.name}`}
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Please select a school to view and manage student promotions
            </div>
          )}
        </div>
      </DialogContent>

      <AlertDialog open={showPromotionDialog} onOpenChange={setShowPromotionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Student Promotion</AlertDialogTitle>
            <AlertDialogDescription>
              This action will promote all eligible students to their next classes. 
              Students in graduating classes will be archived. This action cannot be undone. 
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePromoteAll}>
              Continue with Promotion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}